/**
 * EmbeddedAIChat — self-contained AI chat panel reused inside the unified
 * fullscreen editor. Mirrors the chat-related state/handlers from
 * `ClientDashboard.EditTab.js` so the credit system, rate-limits, image
 * upload, history, restore, etc. all keep working identically.
 *
 * Renders the existing <ChatPanel/> inside a 350x670 floating frame.
 * Parent triggers `onIframeReload()` whenever a chat change is applied,
 * so the visual editor canvas refreshes.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Bot, ShoppingCart } from "lucide-react";
import { ChatPanel } from "../../pages/ClientDashboard.ChatPanel";
import { CreditsExhausted } from "../../pages/ClientDashboard.Controls";
import { BuyNowBanner } from "../../pages/ClientDashboard.BuyNowBanner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function EmbeddedAIChat({
  token, account, mods, onModsRefresh, siteSlug, offerExpiresAt, settings,
  open, onClose, onIframeReload, previewHtml: extPreviewHtml, onPreviewHtml,
  applyOps, onUndo,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [attachedImage, setAttachedImage] = useState(null);
  const [siteImage, setSiteImage] = useState(null);
  const [thinkingStage, setThinkingStage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingProposal, setPendingProposal] = useState(null);
  const [prevInstruction, setPrevInstruction] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [acceptingTimer, setAcceptingTimer] = useState(0);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [localMods, setLocalMods] = useState(mods);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [restoring, setRestoring] = useState(false);
  // Iter88: agent v2 is now the only agent. The toggle was removed —
  // legacy AST/proposal stays as a hidden internal fallback for cases
  // the agent can't handle.
  const agentVersion = "v2";
  const [closingChat, setClosingChat] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [, setShowMobilePreview] = useState(false);
  const [checkpoints, setCheckpoints] = useState([]);
  // Active AbortController for the current AI request — exposed so the
  // "Detener" button (ported from grapesjs-ai-copilot's Stop link) can
  // cancel an in-flight request without waiting for the 90s timeout.
  const activeAbortRef = useRef(null);
  // Plan-mode (Iter88): when the agent proposes a plan, we stash it here
  // so the next sendMessage() injects `approved_plan` and tells the agent
  // to skip Stage 1 and execute.
  const pendingPlanRef = useRef(null);

  const messagesEndRef = useRef(null);
  const thinkTimers = useRef([]);
  const inputRef = useRef(null);
  const pollTimerRef = useRef(null);

  const initials = account?.name
    ? account.name.split(" ").slice(0, 2).map(n => n[0]).join("").toUpperCase()
    : "TU";

  useEffect(() => { setLocalMods(mods); }, [mods]);

  // Bubble previewHtml up so the parent canvas can show it if desired
  useEffect(() => { onPreviewHtml?.(previewHtml); }, [previewHtml, onPreviewHtml]);

  useEffect(() => {
    fetch(`${API}/client/${token}/chat/history`)
      .then(r => r.json())
      .then(d => {
        const hist = d.messages || [];
        setCheckpoints(d.checkpoints || []);
        if (hist.length === 0) {
          setMessages([{
            id: "welcome", role: "agent", type: "message", timestamp: new Date().toISOString(),
            content: `¡Hola${account?.name ? ` ${account.name.split(" ")[0]}` : ""}! Soy tu asistente de sitio web. Podés pedirme cambios en textos, colores, datos de contacto y más. También podés adjuntar una captura para mostrarme exactamente qué querés cambiar. ¿Qué querés modificar hoy?`,
          }]);
        } else {
          setMessages(hist);
          const lastPending = [...hist].reverse().find(m => m.type === "proposal" && m.status === "pending");
          if (lastPending) {
            setPendingProposal({ pending_id: lastPending.pending_id });
            setPrevInstruction(lastPending.instruction || "");
          }
        }
      }).catch(() => {});
    fetch(`${API}/client/${token}/chat/rate-limit`)
      .then(r => r.json())
      .then(d => setRateLimitInfo({ ...d, is_limited: !d.allowed }))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, thinkingStage]);

  const clearThinkTimers = () => { thinkTimers.current.forEach(clearTimeout); thinkTimers.current = []; };
  const stopPreviewPoll = () => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
  };

  const startPreviewPoll = useCallback((pendingId) => {
    stopPreviewPoll();
    setPreviewHtml(null);
    setPreviewLoading(true);
    let attempts = 0;
    const maxAttempts = 40;
    pollTimerRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API}/client/${token}/chat/preview-status?pending_id=${pendingId}`);
        if (!res.ok) { stopPreviewPoll(); setPreviewLoading(false); return; }
        const d = await res.json();
        if (d.not_found) {
          stopPreviewPoll(); setPreviewLoading(false);
          toast.info("La propuesta anterior fue reemplazada.");
          return;
        }
        if (d.ready) {
          stopPreviewPoll(); setPreviewLoading(false);
          setPreviewHtml(d.html || null);
          if (d.no_change) {
            const reason = d.edit_status === "structural"
              ? "Este cambio requiere agregar estructura nueva. Contactá soporte."
              : d.error
              ? `Error al aplicar: ${d.error}`
              : "La IA no encontró el elemento exacto. Sé más específico (ej: 'Cambiá el texto X por Y').";
            toast.info(reason, { duration: 9000 });
            setPendingProposal(null);
          } else {
            toast.success("Vista previa lista. Revisá y confirmá en el chat.");
          }
          return;
        }
      } catch { /* keep polling */ }
      if (attempts >= maxAttempts) {
        stopPreviewPoll(); setPreviewLoading(false);
        toast.warning("La generación tardó más de lo esperado.");
      }
    }, 5000);
  }, [token]);

  useEffect(() => () => stopPreviewPoll(), []);

  // Iter91.3 — auto-resume after big-change quote payment.
  // ClientDashboard's /verify handler stashed the instruction + quote_id in
  // sessionStorage and redirected to /editor/{token}?resume_quote=...
  // We auto-fire the original instruction so the agent picks it up with
  // 30 turns instead of 15.
  useEffect(() => {
    let stashed = null;
    try {
      stashed = sessionStorage.getItem("sy_paid_quote_instruction");
      if (stashed) sessionStorage.removeItem("sy_paid_quote_instruction");
    } catch {}
    if (!stashed) return;
    // Iter91.4 — fire the instruction directly to dodge stale closure
    // (was: setInput + setTimeout, which captured the old sendMessage).
    const t = setTimeout(() => sendMessage(stashed), 200);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendMessage = async (overrideInstruction = null, opts = {}) => {
    // Iter91.4 — accept overrideInstruction so handlers like handleApprovePlan
    // can fire without depending on `input` state (which has stale closure).
    // opts:
    //   skipUserBubble: don't render a new user message (used when re-running
    //     a plan the user already approved; their original message is still
    //     visible in history).
    const fromState = input.trim();
    // Guard: callers like the Send button's onClick may accidentally pass the
    // DOM/synthetic event as the first arg. Only honour real string overrides.
    const override = typeof overrideInstruction === "string" ? overrideInstruction : "";
    const instruction = override.trim() || fromState;
    if (!instruction && !attachedImage && !siteImage) return;
    if (isLoading) return;
    if (rateLimitInfo?.is_limited) return;
    const displayContent = instruction || (attachedImage ? "[Imagen adjunta]" : "[Foto para el sitio]");
    if (!opts.skipUserBubble) {
      const userMsg = { id: Date.now().toString(), role: "user", type: "message",
        content: displayContent, timestamp: new Date().toISOString(),
        has_image: !!attachedImage || !!siteImage };
      setMessages(prev => [...prev, userMsg]);
    }
    setInput("");
    const imageToSend = attachedImage; const siteImageToSend = siteImage;
    setAttachedImage(null); setSiteImage(null);
    setIsLoading(true); setThinkingStage(0);
    clearThinkTimers();
    [500, 1000, 1800].forEach((delay, i) => {
      thinkTimers.current.push(setTimeout(() => setThinkingStage(i + 1), delay));
    });
    try {
      const body = { instruction: instruction || "Aplicá los cambios mostrados en la imagen" };
      if (imageToSend) { body.image_base64 = imageToSend.base64; body.image_mime = imageToSend.mime; }
      if (siteImageToSend) {
        try {
          const upRes = await fetch(`${API}/client/${token}/upload-image`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ data_base64: siteImageToSend.base64, mime: siteImageToSend.mime || "image/jpeg", filename: siteImageToSend.name || "upload.jpg" }),
          });
          if (upRes.ok) { const upData = await upRes.json(); if (upData.image_url) body.upload_image_url = upData.image_url; }
        } catch {}
      }
      if (pendingProposal?.pending_id) {
        try {
          await fetch(`${API}/client/${token}/chat/reject`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ pending_id: pendingProposal.pending_id }),
          });
        } catch {}
        stopPreviewPoll(); setPreviewHtml(null); setPendingProposal(null);
      }

      // ─── Iter86: Agent v2 (Claude Sonnet 4.5) ────────────────────────────
      // When the user has opted in (default), route every message through
      // the new agent. It reads/edits the HTML via tools and either
      // applies + summarises, or returns an error. We refresh the iframe
      // via onIframeReload to pick up the new HTML.
      if (agentVersion === "v2") {
        const v2Abort = new AbortController();
        activeAbortRef.current = v2Abort;
        // Iter91.3 — if the user just paid a big-change quote, lift the
        // turn cap by including the quote_id in this single call. Cleared
        // immediately so subsequent messages bill normal credits.
        let paidQuoteId = null;
        try {
          paidQuoteId = sessionStorage.getItem("sy_paid_quote_id") || null;
          if (paidQuoteId) sessionStorage.removeItem("sy_paid_quote_id");
        } catch {}
        try {
          const v2Res = await fetch(`${API}/client/${token}/agent/v2/chat`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              instruction: instruction,
              approved_plan: pendingPlanRef.current?.approved_plan || null,
              paid_quote_id: paidQuoteId,
            }),
            signal: v2Abort.signal,
          });
          // Always clear the just-used approved plan so the next
          // free-text message starts fresh.
          pendingPlanRef.current = null;
          const v2Data = await v2Res.json();
          clearThinkTimers(); setThinkingStage(null); setIsLoading(false);
          if (!v2Res.ok) {
            toast.error(v2Data?.detail || "El agente no pudo responder");
            return;
          }
          if (v2Data?.rate_limited) {
            toast.error(v2Data.message);
            return;
          }
          if (v2Data?.injection_detected) {
            toast.error(v2Data.message);
            return;
          }
          if (v2Data?.locked) {
            setLocalMods({ ...localMods, is_locked: true });
            toast.error("Sin créditos. Comprá más para seguir editando.");
            return;
          }
          // Stage 1: agent returned a plan that needs approval. Don't
          // refresh the iframe (no edits yet) — just render the plan.
          if (v2Data?.needs_approval && v2Data?.plan) {
            setMessages(prev => [...prev, {
              id: Date.now().toString(), role: "agent", type: "agent_v2_plan",
              content: v2Data.summary,
              plan: v2Data.plan,
              instruction: instruction,
              credits_billed: v2Data.credits_billed,
              timestamp: new Date().toISOString(),
            }]);
            return;
          }
          // Iter91.3 — Big-change quote required (agent ran out of turns).
          if (v2Data?.needs_big_change_quote && v2Data?.quote) {
            setMessages(prev => [...prev, {
              id: Date.now().toString(), role: "agent", type: "agent_v2_big_change",
              content: v2Data.summary,
              quote: v2Data.quote,
              instruction: instruction,
              actions: v2Data.actions || [],
              applied: v2Data.applied,
              timestamp: new Date().toISOString(),
            }]);
            if (v2Data.applied) {
              onModsRefresh?.();
              onIframeReload?.();
            }
            return;
          }
          // Stage 2 (or trivial single-pass): edits applied or done.
          setMessages(prev => [...prev, {
            id: Date.now().toString(), role: "agent", type: "agent_v2_done",
            content: v2Data.summary,
            actions: v2Data.actions || [],
            credits_billed: v2Data.credits_billed,
            applied: v2Data.applied,
            instruction: instruction,
            error: v2Data.error,
            timestamp: new Date().toISOString(),
          }]);
          if (v2Data.applied) {
            onModsRefresh?.();
            onIframeReload?.();
          }
          return;
        } catch (e) {
          if (e?.name === "AbortError") {
            activeAbortRef.current = null;
            return;
          }
          clearThinkTimers(); setThinkingStage(null); setIsLoading(false);
          toast.error("Error de red contactando al agente");
          // eslint-disable-next-line no-console
          console.warn("[agent-v2] failed:", e);
          return;
        }
      }

      // ─── AST fast-path (Iter78) ────────────────────────────────────────
      // Try the structured AST planner first. If it returns ops we apply
      // them via PATCH /ast (instant, validated). If it can't (fallback:
      // true) we transparently fall through to the legacy HTML pipeline.
      try {
        // Register AbortController so the Stop button can cancel.
        const astAbort = new AbortController();
        activeAbortRef.current = astAbort;
        const astRes = await fetch(`${API}/client/${token}/chat/ast-propose`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal: astAbort.signal,
        });
        const astData = await astRes.json();
        if (astRes.ok && astData?.ops?.length > 0) {
          // Apply ops via the editor's own pipeline when available — that
          // path also pushes to the undo stack and morph-diffs the iframe
          // (no full reload). Falls back to a direct PATCH when this chat
          // is rendered outside the unified editor (legacy dashboard).
          let patchOk = true;
          if (typeof applyOps === "function") {
            try {
              await applyOps(astData.ops);
            } catch {
              patchOk = false;
            }
          } else {
            const patchRes = await fetch(`${API}/client/${token}/ast`, {
              method: "PATCH", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ ops: astData.ops }),
            });
            if (!patchRes.ok) patchOk = false;
          }
          clearThinkTimers(); setThinkingStage(null); setIsLoading(false);
          if (!patchOk) {
            toast.error(`No se pudieron aplicar los cambios.`);
            return;
          }
          if (astData.rate_limit) {
            setRateLimitInfo({ ...astData.rate_limit, is_limited: !astData.rate_limit.allowed });
          }
          setMessages(prev => [...prev, {
            id: Date.now().toString(), role: "agent", type: "ast_ops_applied",
            content: astData.summary || `${astData.ops.length} cambio(s) aplicado(s).`,
            ops_count: astData.ops.length,
            ops: astData.ops,                     // for "didn't work" undo
            instruction: instruction || "(imagen)", // for retry context
            timestamp: new Date().toISOString(),
          }]);
          // Refresh credits so the badge updates. Skip onIframeReload — the
          // applyOps morphdom path keeps the iframe content fresh in place.
          onModsRefresh?.();
          if (typeof applyOps !== "function") onIframeReload?.();
          return;
        }
        // fallback === true → keep going to the legacy /chat/propose flow
      } catch (e) {
        // User-initiated abort (Stop button) → bail cleanly, no fallback,
        // no error toast. handleStop already added the "Cancelado" bubble.
        if (e?.name === "AbortError") {
          activeAbortRef.current = null;
          return;
        }
        // Network error on the AST endpoint should not block the legacy flow.
        // eslint-disable-next-line no-console
        console.warn("[ai-chat] ast-propose failed, falling back:", e);
      }

      const abortCtrl = new AbortController();
      activeAbortRef.current = abortCtrl;
      const abortTimeout = setTimeout(() => abortCtrl.abort(), 90_000);
      let res;
      try {
        res = await fetch(`${API}/client/${token}/chat/propose`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body), signal: abortCtrl.signal,
        });
      } finally { clearTimeout(abortTimeout); }
      let d;
      try { d = await res.json(); }
      catch {
        clearThinkTimers(); setThinkingStage(null); setIsLoading(false);
        toast.error(`Error del servidor (${res.status}).`);
        return;
      }
      if (!res.ok) {
        clearThinkTimers(); setThinkingStage(null); setIsLoading(false);
        toast.error(d?.detail || d?.message || `Error ${res.status}`);
        return;
      }
      clearThinkTimers(); setThinkingStage(null); setIsLoading(false);
      if (d.rate_limit) setRateLimitInfo({ ...d.rate_limit, is_limited: !d.rate_limit.allowed });
      if (d.rate_limited) {
        setRateLimitInfo({ allowed: false, is_limited: true, wait_seconds: d.wait_seconds, reset_at: d.reset_at, remaining: 0 });
        setMessages(prev => prev.slice(0, -1));
        toast.error(d.message || "Límite alcanzado.");
        return;
      }
      if (d.injection_detected) {
        setMessages(prev => [...prev, { id: Date.now().toString(), role: "agent", type: "message", timestamp: new Date().toISOString(), content: d.message }]);
        return;
      }
      if (d.locked) {
        setMessages(prev => [...prev, { id: Date.now().toString(), role: "agent", type: "message", timestamp: new Date().toISOString(),
          content: "Llegaste al límite de modificaciones gratuitas. Comprá un paquete para continuar." }]);
        setLocalMods(m => ({ ...m, is_locked: true, total_available: 0 }));
        return;
      }
      if (d.suggest_inline_edit) {
        setMessages(prev => [...prev, { id: Date.now().toString(), role: "agent", type: "inline_edit_hint", timestamp: new Date().toISOString(), content: d.message }]);
        return;
      }
      if (d.is_large_change) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(), role: "agent", type: "pricing_proposal", timestamp: new Date().toISOString(),
          instruction, status: "pending", current_price: d.current_price, additional_price: d.additional_price,
          new_total: d.new_total, current_lines: d.current_lines, estimated_new_lines: d.estimated_new_lines,
        }]);
        return;
      }
      const proposalMsg = {
        id: Date.now().toString(), role: "agent", type: "proposal",
        content: "Revisé tu sitio y preparé los cambios. Acá está el resumen:",
        changes: d.changes, pending_id: d.pending_id, status: "pending", timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, proposalMsg]);
      setPendingProposal({ pending_id: d.pending_id });
      setPrevInstruction(instruction);
      startPreviewPoll(d.pending_id);
    } catch (err) {
      clearThinkTimers(); setThinkingStage(null); setIsLoading(false);
      toast.error(err?.name === "AbortError" ? "Tardó demasiado." : `Error: ${err?.message || ""}`);
    }
  };

  const acceptProposal = async (pendingId, msg) => {
    setAccepting(true); setAcceptingTimer(0);
    const timerInterval = setInterval(() => setAcceptingTimer(s => s + 1), 1000);
    const abortCtrl = new AbortController();
    const abortTimeout = setTimeout(() => abortCtrl.abort(), 150_000);
    try {
      let res;
      try {
        res = await fetch(`${API}/client/${token}/chat/accept`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pending_id: pendingId, instruction: prevInstruction, changes: msg?.changes || [] }),
          signal: abortCtrl.signal,
        });
      } finally { clearTimeout(abortTimeout); }
      let d;
      try { d = await res.json(); } catch { toast.error(`Error inesperado (${res.status}).`); return; }
      if (res.status === 402) { toast.error("Sin modificaciones."); return; }
      if (res.status === 503 && d?.detail?.code === "AI_PROCESSING") {
        toast.info("La IA está preparando los cambios.", { duration: 8000 }); return;
      }
      if (!res.ok) { toast.error(d?.detail || d?.message || "No se pudo aplicar."); return; }
      setMessages(prev => prev.map(m => m.pending_id === pendingId ? { ...m, status: "applied" } : m));
      setMessages(prev => [...prev, {
        id: `applied-${Date.now()}`, role: "agent", type: "applied_proposal",
        timestamp: new Date().toISOString(), pending_id: pendingId, instruction: prevInstruction,
        changes: msg?.changes || [], checkpoint_num: d.checkpoint_num, status: "pending_confirm",
      }]);
      setPendingProposal(null);
      setLocalMods(m => ({ ...m, total_available: d.credits_remaining, is_locked: d.is_locked }));
      onModsRefresh?.();
      if (d.html_changed === false) {
        toast.warning("Sin cambios detectados. Sé más específico.");
      } else {
        toast.success("Cambio aplicado. Tu sitio se actualizó.");
      }
      setPreviewHtml(null);
      onIframeReload?.();
      fetch(`${API}/client/${token}/chat/history`)
        .then(r => r.json()).then(d => setCheckpoints(d.checkpoints || []))
        .catch(() => {});
    } catch (err) {
      toast.error(err?.name === "AbortError" ? "La IA tardó demasiado." : "Error al aplicar.");
    } finally {
      clearInterval(timerInterval); setAcceptingTimer(0); setAccepting(false);
    }
  };

  const confirmApplied = async (appliedMsg) => {
    try {
      await fetch(`${API}/client/${token}/chat/confirm`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pending_id: appliedMsg.pending_id, instruction: appliedMsg.instruction,
          changes: appliedMsg.changes || [], checkpoint_num: appliedMsg.checkpoint_num || 0,
        }),
      });
    } catch {}
    setMessages(prev => prev.map(m => m.id === appliedMsg.id ? { ...m, status: "confirmed", type: "applied_confirmed" } : m));
    setMessages(prev => [...prev,
      { id: `log-${Date.now()}`, role: "agent", type: "accept_log", timestamp: new Date().toISOString(),
        instruction: appliedMsg.instruction, checkpoint_num: appliedMsg.checkpoint_num, changes: appliedMsg.changes || [] },
      { id: `fw-${Date.now()}`, role: "agent", type: "message", timestamp: new Date().toISOString(),
        content: "Cambio confirmado. ¿Querés hacer otra modificación?" },
    ]);
    setPreviewHtml(null);
    toast.success("Confirmado.");
  };

  const restoreVersion = async (msgOrCheckpointNum) => {
    setRestoring(true);
    const checkpointNum = typeof msgOrCheckpointNum === "number" ? msgOrCheckpointNum : msgOrCheckpointNum?.checkpoint_num;
    try {
      const res = await fetch(`${API}/client/${token}/chat/restore`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(checkpointNum ? { checkpoint_num: checkpointNum } : {}),
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || "No hay versión anterior"); return; }
      if (typeof msgOrCheckpointNum === "object") {
        setMessages(prev => prev.map(m => m.id === msgOrCheckpointNum.id ? { ...m, type: "applied_confirmed" } : m));
      }
      setMessages(prev => [...prev, {
        id: `rst-${Date.now()}`, role: "agent", type: "restore_log", timestamp: new Date().toISOString(),
        content: checkpointNum ? `Restaurado al estado previo al cambio #${checkpointNum}.` : "Versión anterior restaurada.",
      }]);
      setPreviewHtml(null); onIframeReload?.();
      toast.success("Sitio restaurado");
    } catch { toast.error("Error al restaurar"); }
    finally { setRestoring(false); }
  };

  const closeChat = async () => {
    if (messages.length <= 2) return;
    setClosingChat(true);
    try {
      const res = await fetch(`${API}/client/${token}/chat/close`, { method: "POST", headers: { "Content-Type": "application/json" } });
      const d = await res.json();
      if (d.success) {
        setMessages([
          { id: "summary", role: "system", type: "session_summary", timestamp: new Date().toISOString(), content: d.summary },
          { id: "new-session", role: "agent", type: "message", timestamp: new Date().toISOString(), content: "Nueva sesión iniciada. ¿En qué puedo ayudarte?" },
        ]);
        toast.success("Chat cerrado y resumen guardado");
      }
    } catch { toast.error("Error al cerrar el chat"); }
    finally { setClosingChat(false); }
  };

  const continueModifying = async (pendingId, prevInstr) => {
    stopPreviewPoll(); setPreviewLoading(false);
    try {
      await fetch(`${API}/client/${token}/chat/reject`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pending_id: pendingId }),
      });
    } catch {}
    setMessages(prev => prev.map(m => m.pending_id === pendingId ? { ...m, status: "rejected" } : m));
    setMessages(prev => [...prev, {
      id: `fw-${Date.now()}`, role: "agent", type: "message", timestamp: new Date().toISOString(),
      content: "Entendido. Ajustá tu pedido y lo proceso de nuevo:",
    }]);
    setPendingProposal(null);
    setInput(prevInstr || "");
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const acceptPricing = async (pricingMsg) => {
    setPricingLoading(true);
    try {
      const res = await fetch(`${API}/client/${token}/chat/upgrade/checkout`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ additional_price: pricingMsg.additional_price, new_total: pricingMsg.new_total, instruction: pricingMsg.instruction, origin_url: window.location.origin }),
      });
      const d = await res.json();
      if (d.checkout_url) {
        setMessages(prev => prev.map(m => m.id === pricingMsg.id ? { ...m, status: "accepted" } : m));
        window.location.href = d.checkout_url;
      } else toast.error("Error al crear el pago.");
    } catch { toast.error("Error de conexión."); }
    finally { setPricingLoading(false); }
  };
  const declinePricing = (pricingMsg) => {
    setMessages(prev => prev.map(m => m.id === pricingMsg.id ? { ...m, status: "declined" } : m));
    setMessages(prev => [...prev, {
      id: `fw-${Date.now()}`, role: "agent", type: "message", timestamp: new Date().toISOString(),
      content: "Entendido. Pedí un cambio más pequeño dentro del sitio actual.",
    }]);
  };

  const isLimited = rateLimitInfo?.is_limited;

  /** Plan approved by the user → re-run the agent with `approved_plan`
   *  set so it skips Stage 1 and goes straight to execution. */
  const handleApprovePlan = (msg) => {
    if (!msg?.plan || !msg?.instruction) return;
    pendingPlanRef.current = { approved_plan: msg.plan };
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, plan_decision: "approved" } : m));
    // Persist the decision so a reload preserves the bubble state.
    fetch(`${API}/client/${token}/agent/v2/plan-decision`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message_id: msg.id, decision: "approved" }),
    }).catch(() => {});
    // Iter91.4 — pass the instruction directly (avoids stale `input` closure)
    // and skip rendering a duplicate user bubble (the original is still in
    // history and the plan bubble is right above us).
    sendMessage(msg.instruction, { skipUserBubble: true });
  };

  /** Plan rejected → just mark the bubble and clear state. */
  const handleRejectPlan = (msg) => {
    pendingPlanRef.current = null;
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, plan_decision: "rejected" } : m));
    fetch(`${API}/client/${token}/agent/v2/plan-decision`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message_id: msg.id, decision: "rejected" }),
    }).catch(() => {});
    setMessages(prev => [...prev, {
      id: `reject-${Date.now()}`, role: "agent", type: "message",
      content: "Listo, descarté el plan. Decime cómo querés enfocarlo de otra forma.",
      timestamp: new Date().toISOString(),
    }]);
  };

  /** "Detener" — cancel the current in-flight AI request.
   *  Ported from grapesjs-ai-copilot (their Stop link). */
  const handleStop = () => {
    try { activeAbortRef.current?.abort(); } catch {}
    activeAbortRef.current = null;
    clearThinkTimers();
    setThinkingStage(null);
    setIsLoading(false);
    setMessages(prev => [...prev, {
      id: `stop-${Date.now()}`, role: "agent", type: "message",
      content: "Cancelado por vos.",
      timestamp: new Date().toISOString(),
    }]);
  };

  /** "Sugerir mejoras" — proactive copilot pattern. Sends a meta-prompt
   *  asking the AI to propose 3 concrete improvements. Routes through
   *  the same /chat/ast-propose pipeline so accepted suggestions land as
   *  ast_ops_applied bubbles with full Loved/DidntWork support. */
  const handleSuggest = () => {
    if (isLoading) return;
    setInput(
      "Sugerí 3 mejoras concretas y específicas que pueda aplicar a este sitio ahora mismo. " +
      "Pensá en accesibilidad, claridad del CTA, jerarquía tipográfica, contraste de colores y " +
      "consistencia del diseño. Aplicá la mejora más impactante directamente."
    );
    setTimeout(() => sendMessage(), 50);
  };

  /** "No funcionó" — copilot-style retry. Undoes the last AST patch and
   *  re-prompts the AI with failure context so it can try a different
   *  approach. Marks the original bubble as `retried` so the button
   *  doesn't reappear (avoids infinite loops). */
  const handleDidntWork = async (originalMsg) => {
    if (!originalMsg?.instruction) {
      toast.error("No tengo el pedido original para reintentar");
      return;
    }
    // Step 1: undo the last applied edit (if the host supports it).
    if (typeof onUndo === "function") {
      try { await onUndo(); } catch {}
    }
    // Step 2: mark the original bubble so the button hides + show a retry
    // log. This gives the user instant feedback that we're working on it.
    setMessages(prev => prev.map(m =>
      m.id === originalMsg.id ? { ...m, retried: true } : m
    ));
    setMessages(prev => [...prev, {
      id: `retry-${Date.now()}`, role: "agent", type: "message",
      content: "Entendido — voy a intentar otro enfoque diferente al anterior.",
      timestamp: new Date().toISOString(),
    }]);
    // Step 3: send a NEW request with explicit failure context. The AST
    // planner sees "the previous attempt did X but the user said it didn't
    // work" and is forced to consider an alternative.
    const newInstruction =
      `${originalMsg.instruction}\n\n` +
      `(Importante: ya intentaste resolver esto y el resultado fue: "${originalMsg.content}". ` +
      `El usuario dice que NO funcionó como esperaba. Probá un enfoque DIFERENTE: ` +
      `por ejemplo, si la última vez tocaste un texto, esta vez tocá el estilo; ` +
      `si tocaste un color, esta vez probá con otro nodo o con otra propiedad.)`;
    // Inject into the input + auto-send. Reuses the existing sendMessage
    // pipeline so credits/rate limits/etc. behave correctly.
    setInput(newInstruction);
    setTimeout(() => sendMessage(), 50);
  };

  /** "Me encantó · usá este estilo" — saves the original instruction as
   *  a style preference so the AST planner uses it as soft context on
   *  every future call. The bubble gets a pink "Recordado" tag so the
   *  user knows the AI now has memory of their taste. */
  const handleLoved = async (originalMsg) => {
    const text = originalMsg?.instruction;
    if (!text) {
      toast.error("No tengo el pedido original para recordar");
      return;
    }
    try {
      const res = await fetch(`${API}/client/${token}/style-preference`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Error guardando preferencia");
      setMessages(prev => prev.map(m =>
        m.id === originalMsg.id ? { ...m, loved_text: text } : m
      ));
      toast.success("Recordado · la IA usará este estilo de aquí en adelante");
    } catch (e) {
      toast.error(e.message || "No se pudo guardar la preferencia");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="embedded-ai-chat"
      className="bg-white flex flex-col overflow-hidden relative h-full border-r border-gray-200"
      style={{ width: "100%" }}
    >
      {/* Top BuyNowBanner — hidden when the credits-exhausted overlay is up
          (the overlay carries its own banner; rendering both blurs ugly through
          the backdrop). */}
      {siteSlug && !account?.is_paid && !(localMods?.total_available <= 0) && (
        <BuyNowBanner token={token} account={account} siteSlug={siteSlug}
          offerExpiresAt={offerExpiresAt} settings={settings}
          modsExhausted={false} />
      )}

      <ChatPanel
        account={account} localMods={localMods} previewHtml={previewHtml}
        setShowMobilePreview={setShowMobilePreview}
        closingChat={closingChat} closeChat={closeChat}
        messages={messages} initials={initials}
        acceptProposal={acceptProposal} continueModifying={continueModifying}
        confirmApplied={confirmApplied} restoreVersion={restoreVersion}
        accepting={accepting} acceptingTimer={acceptingTimer}
        restoring={restoring} prevInstruction={prevInstruction}
        isLoading={isLoading} thinkingStage={thinkingStage}
        messagesEndRef={messagesEndRef} rateLimitInfo={rateLimitInfo}
        token={token} inputRef={inputRef}
        input={input} setInput={setInput} sendMessage={sendMessage}
        attachedImage={attachedImage} setAttachedImage={setAttachedImage}
        pricingLoading={pricingLoading}
        acceptPricing={acceptPricing} declinePricing={declinePricing}
        isLimited={isLimited} pendingProposal={pendingProposal}
        offerExpiresAt={offerExpiresAt} settings={settings} siteSlug={siteSlug}
        onMinimize={onClose}
        checkpoints={checkpoints} previewLoading={previewLoading}
        siteImage={siteImage} onSiteImageAttach={setSiteImage}
        onSiteImageRemove={() => setSiteImage(null)}
        onDidntWork={handleDidntWork}
        onLoved={handleLoved}
        onStop={handleStop}
        onSuggest={handleSuggest}
        onApprovePlan={handleApprovePlan}
        onRejectPlan={handleRejectPlan}
      />

      {localMods?.total_available <= 0 && (
        <div className="absolute inset-0 z-30 flex flex-col bg-[#0A0A0A]/96 backdrop-blur-[6px]">
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#ffc700]/10 border border-[#ffc700]/30 flex items-center justify-center mb-4">
              <ShoppingCart size={28} className="text-[#ffc700]" />
            </div>
            <h3 className="text-xl font-black text-white mb-1">Prueba gratuita terminada</h3>
            <p className="text-sm text-gray-400 mb-2">Usaste tus modificaciones gratuitas.</p>
            <p className="text-sm font-semibold text-gray-300 mb-6">Comprá ahora para seguir editando tu sitio.</p>
            {siteSlug && account && (
              <BuyNowBanner token={token} account={account} siteSlug={siteSlug}
                offerExpiresAt={offerExpiresAt} settings={settings} modsExhausted fullWidth />
            )}
            <CreditsExhausted token={token} />
          </div>
        </div>
      )}
    </div>
  );
}

/** Floating bubble shown when the chat is closed.
 *  Yellow brand bubble — matches Sitios YA accent (#FFC107). Sits at the
 *  bottom-left of the editor canvas (above the iframe) so it's always
 *  reachable and out of the way of the right Properties panel.
 */
export function AIChatBubble({ onOpen }) {
  return (
    <button
      data-testid="ai-chat-bubble"
      onClick={onOpen}
      title="Abrir asistente IA"
      className="fixed bottom-6 left-24 z-[60] w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 group"
      style={{
        background: "linear-gradient(135deg, #FFC107 0%, #FFD54F 100%)",
        boxShadow: "0 6px 24px rgba(255,193,7,0.55), 0 0 0 1px rgba(0,0,0,0.05)",
      }}
    >
      <Bot size={22} className="text-black" strokeWidth={2.5} />
      <span className="absolute inset-0 rounded-full border-2 border-yellow-300/70 animate-ping group-hover:hidden" />
    </button>
  );
}
